import { useState, useEffect } from "react";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "../api";
import { useDebouncedValue } from "../lib/useDebouncedValue.js";
import PatientAlias from "./PatientAlias.jsx";

// Admin-only escape hatch for a scan saved under the wrong patient (see backend's
// PATCH /:id/reassign-patient) — search-and-pick, same debounced-server-search pattern as the
// scanner's own patient picker in App.jsx, just scoped to this one modal instead of a page.
export default function ReassignPatientModal({ doc, currentPatientName, onClose, onReassigned }) {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!debouncedQuery.trim()) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    api.listPatients({ q: debouncedQuery, limit: 10 })
      .then(r => { if (!cancelled) setResults(r.items.filter(p => p.id !== doc.patientId)); })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQuery, doc.patientId]);

  const handlePick = async (patient) => {
    setSavingId(patient.id);
    setError("");
    try {
      await api.reassignDocumentPatient(doc.id, patient.id);
      onReassigned(patient);
    } catch (e) {
      setError(e.response?.data?.error || "Не удалось перенести скан");
      setSavingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[250] flex items-center justify-center p-4"
      onMouseDown={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-[420px] max-w-[95vw] overflow-hidden flex flex-col" style={{ maxHeight: "80vh" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div>
            <div className="text-base font-bold text-slate-900">Перенести скан другому пациенту</div>
            <div className="text-xs text-slate-400 mt-0.5">Сейчас: {currentPatientName || "—"}</div>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              autoFocus
              className="pl-8 h-9 text-sm"
              placeholder="Имя или телефон пациента..."
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-2 py-2">
          {loading && <div className="text-xs text-slate-400 px-3 py-2">Поиск...</div>}
          {!loading && debouncedQuery.trim() && results.length === 0 && (
            <div className="text-xs text-slate-400 px-3 py-2">Никого не найдено</div>
          )}
          {!debouncedQuery.trim() && (
            <div className="text-xs text-slate-400 px-3 py-2">Начните вводить имя или телефон</div>
          )}
          {results.map(p => (
            <button
              key={p.id}
              disabled={savingId !== null}
              onClick={() => handlePick(p)}
              className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors flex items-center justify-between gap-2"
            >
              <span className="flex flex-col">
                <span className="font-medium text-slate-800 text-sm">{p.name}<PatientAlias name={p.name} alias={p.alias} /></span>
                {p.phone && <span className="text-slate-400 text-xs">{p.phone}</span>}
              </span>
              {savingId === p.id && <span className="text-xs text-slate-400">...</span>}
            </button>
          ))}
        </div>

        {error && (
          <div className="px-5 py-2.5 text-xs text-red-600 border-t border-red-100 bg-red-50 flex-shrink-0">{error}</div>
        )}
      </div>
    </div>
  );
}
